// filepath: d:\Aa\Aa apps rarr\WebApps\rarr-system\src\app\services\repacking-state.service.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ParcelDto, SenderInfoDto, ReceiverInfoDto, PreCarrierInfoDto, NextCarrierInfoDto } from '../../models/order.dto'; // Adjust this path if your ParcelDto is located elsewhere

// Interface for details from the original (cancelled) carton that can be reused
export interface ReusableCartonConfiguration {
  destinationRegion?: string | null; // Allow null if DTOs use it
  cartonWeight?: number | null | undefined; // Allow null
  cartonLength?: number | null;
  cartonWidth?: number | null;
  cartonHeight?: number | null;
  cartonVolume?: number | null; // Added volume for completeness
  goodsType?: number | null | undefined;    // Allow null
  transportType?: number | null | undefined; // Allow null
  serviceCode?: string | null | undefined;   // Allow null
}

// Interface for the state of parcels pending repack
export interface PendingRepackState {
  parcelsForRepack: ParcelDto[];
  /** The unique identifier (e.g., unique_id) of the original order/shipment these parcels belong to,
   * or the ID of the cancelled carton if that's more relevant as the source of data. */
  sourceOrderUniqueId: string;

  /** The ILH Shopee No (or similar system-specific ID) of the *cancelled* repacked carton, for reference. */
  cancelledCartonIlhShopeeNo: string;

  /** Sender information to be pre-filled for the new repacked carton. */
  senderToUse?: SenderInfoDto;

  /** Receiver information to be pre-filled for the new repacked carton. */
  receiverToUse?: ReceiverInfoDto;

  /** Pre-carrier information to be pre-filled for the new repacked carton. */
  preCarrierToUse?: PreCarrierInfoDto;

  /** Next-carrier information to be pre-filled for the new repacked carton. */
  nextCarrierToUse?: NextCarrierInfoDto;

  /** Configuration details (dimensions, weight, service codes) from the cancelled carton to pre-fill. */
  cartonConfigToReuse?: ReusableCartonConfiguration;
}

// Add this interface at the top with your other interfaces
export interface TargetCartonInfo {
  cartonNo: string;
  shopeeOrderNo?: string;
  serviceCode?: string;
  destinationRegion?: string;
  status?: string; // Optional, if you want to track the status of the target carton
}

// Add a new interface for pending rebind state
export interface PendingRebindState {
  hasPendingRebind: boolean;
  timestamp: number; // When the unbinding operation was started
  parcelsToRebind: ParcelDto[];
}

@Injectable({
  providedIn: 'root' // This makes the service a singleton available application-wide
})
export class RepackingStateService {
  private pendingRepackStateSubject = new BehaviorSubject<PendingRepackState | null>(null);
  public pendingRepackState$: Observable<PendingRepackState | null> = this.pendingRepackStateSubject.asObservable();

  private targetCartonInfoSubject = new BehaviorSubject<TargetCartonInfo | null>(null);
  public targetCartonInfo$: Observable<TargetCartonInfo | null> = this.targetCartonInfoSubject.asObservable();

  // Add a new subject for pending rebind state
  private pendingRebindStateSubject = new BehaviorSubject<PendingRebindState>({
    hasPendingRebind: false,
    timestamp: 0,
    parcelsToRebind: []
  });
  public pendingRebindState$: Observable<PendingRebindState> = this.pendingRebindStateSubject.asObservable();

  constructor() {
    // Optional: If you want state to persist across browser sessions/refreshes,
    // you can load it from localStorage here.
    this.loadStateFromLocalStorage();
  }

  // Methods for pendingRepackState
  setPendingRepackState(state: PendingRepackState): void {
    this.pendingRepackStateSubject.next(state);
    // Optional: Save to localStorage if you want persistence across sessions
    // localStorage.setItem('pendingRepackState', JSON.stringify(state));
  }

  getPendingRepackState(): PendingRepackState | null {
    // If not using localStorage and relying on BehaviorSubject for current session:
    return this.pendingRepackStateSubject.getValue();

    // If using localStorage to ensure state is reloaded even if service is re-instantiated (e.g. after refresh)
    // const storedState = localStorage.getItem('pendingRepackState');
    // if (storedState) {
    //   this.pendingRepackStateSubject.next(JSON.parse(storedState));
    // }
    // return this.pendingRepackStateSubject.getValue();
  }

  clearPendingRepackState(): void {
    this.pendingRepackStateSubject.next(null);
    // Optional: Clear from localStorage
    // localStorage.removeItem('pendingRepackState');
  }

  // Methods for targetCartonInfo
  setTargetCartonInfo(info: TargetCartonInfo): void {
    this.targetCartonInfoSubject.next(info);
    // Optional: Save to localStorage if you want persistence across sessions
    localStorage.setItem('target_carton', JSON.stringify(info));
  }

  getTargetCartonInfo(): TargetCartonInfo | null {
    // If using localStorage to ensure state is reloaded even after refresh
    const storedInfo = localStorage.getItem('arget_carton');
    if (storedInfo) {
      try {
        const parsedInfo = JSON.parse(storedInfo);
        this.targetCartonInfoSubject.next(parsedInfo);
        return parsedInfo;
      } catch (e) {
        console.error('Error parsing target carton info from localStorage', e);
      }
    }
    return this.targetCartonInfoSubject.getValue();
  }

  clearTargetCartonInfo(): void {
    this.targetCartonInfoSubject.next(null);
    // Remove from localStorage
    localStorage.removeItem('rarr_target_carton');
  }

  // Add methods for pendingRebindState
  setPendingRebindState(parcels: ParcelDto[]): void {
    const pendingRebindState: PendingRebindState = {
      hasPendingRebind: parcels.length > 0,
      timestamp: Date.now(),
      parcelsToRebind: parcels
    };
    this.pendingRebindStateSubject.next(pendingRebindState);
    localStorage.setItem('pendingRebindState', JSON.stringify(pendingRebindState));
  }

  getPendingRebindState(): PendingRebindState {
    return this.pendingRebindStateSubject.getValue();
  }

  clearPendingRebindState(): void {
    const emptyState: PendingRebindState = {
      hasPendingRebind: false,
      timestamp: 0,
      parcelsToRebind: []
    };
    this.pendingRebindStateSubject.next(emptyState);
    localStorage.removeItem('pendingRebindState');
  }

  hasPendingRebind(): boolean {
    const state = this.pendingRebindStateSubject.getValue();
    return state.hasPendingRebind && state.parcelsToRebind.length > 0;
  }

  // Load state from localStorage
  private loadStateFromLocalStorage(): void {
    // Load pending rebind state
    const storedRebindState = localStorage.getItem('pendingRebindState');
    if (storedRebindState) {
      try {
        const parsedState = JSON.parse(storedRebindState);
        this.pendingRebindStateSubject.next(parsedState);
      } catch (e) {
        console.error('Error parsing pending rebind state from localStorage', e);
      }
    }

    // Load pending repack state
    const storedRepackState = localStorage.getItem('pendingRepackState');
    if (storedRepackState) {
      try {
        const parsedState = JSON.parse(storedRepackState);
        this.pendingRepackStateSubject.next(parsedState);
      } catch (e) {
        console.error('Error parsing pending repack state from localStorage', e);
      }
    }

    // Load target carton info
    const storedTargetInfo = localStorage.getItem('target_carton');
    if (storedTargetInfo) {
      try {
        const parsedInfo = JSON.parse(storedTargetInfo);
        this.targetCartonInfoSubject.next(parsedInfo);
      } catch (e) {
        console.error('Error parsing target carton info from localStorage', e);
      }
    }
  }
}